import { useEffect, useState } from 'react';
import { sanitizeValue } from '../../lib/sanitize';
import { useFetch } from '../useFetch';
import { useOperator } from '../../contexts/OperatorContext';
import { useSort } from '../useSort';
import { BASE_URL_MAP } from '../../lib/environment';

type Action =
  | ''
  | 'addLocation'
  | 'getLocation'
  | 'getLocations'
  | 'updateLocation';

export const useLocations = () => {
  const [action, setAction] = useState<Action>('');
  const [location, setLocation] = useState<AccountLocation | null>(null);
  const [locations, setLocations] = useState<AccountLocation[]>([]);
  const { loading, makeRequest, response } = useFetch();
  const {
    id: operatorId,
    locations: operatorLocations,
    updateOperatorState,
  } = useOperator();
  const { sortAsc } = useSort<{ name: string }>('name');
  const BASE_URL = `${BASE_URL_MAP['Hospitality']}/${operatorId}/Locations`;

  useEffect(() => {
    resetState();
  }, [operatorId]);

  useEffect(() => {
    if (response && !loading) {
      if (action === 'updateLocation') {
        const mappedLocation = mapLocation(response);
        updateOperatorState(
          'locations',
          [
            mappedLocation,
            ...(operatorLocations ?? []).filter(
              (location) => location.id !== mappedLocation.id
            ),
          ].sort(sortAsc)
        );
      } else if (action === 'addLocation') {
        const mappedLocation = mapLocation(response);

        updateOperatorState(
          'locations',
          [mappedLocation, ...(operatorLocations ?? [])].sort(sortAsc)
        );
      } else if (action === 'getLocation') setLocation(mapLocation(response));
      else if (action === 'getLocations')
        setLocations(
          response.map((location: ApiAccountLocation) => mapLocation(location))
        );
    }
    // eslint-disable-next-line
  }, [loading, response]);

  const addLocation = (location: CreateAccountLocation) => {
    setAction('addLocation');
    const body = mapApiLocation(location);

    makeRequest(BASE_URL, 'post', sanitizeValue(body));
  };

  const getLocation = (locationId: string) => {
    setAction('getLocation');
    makeRequest(`${BASE_URL}/${locationId}`, 'get');
  };

  const getLocations = () => {
    setAction('getLocations');
    makeRequest(BASE_URL, 'get');
  };

  const updateLocation = (location: AccountLocation) => {
    setAction('updateLocation');
    const body: ApiCreateAccountLocation = sanitizeValue({
      Active: location.active,
      Name: location.name,
    });

    makeRequest(`${BASE_URL}/${location.id}`, 'put', body);
  };

  const resetState = () => {
    setAction('');
    setLocation(null);
    setLocations([]);
  };

  return {
    addLocation,
    location,
    locations,
    getLocation,
    getLocations,
    loading,
    updateLocation,
  };
};

const mapApiLocation = (
  location: CreateAccountLocation
): ApiCreateAccountLocation => ({
  Active: location.active,
  Name: location.name,
});

export const mapLocation = (location: ApiAccountLocation): AccountLocation => ({
  id: location.ID,
  active: location.Active,
  name: location.Name,
});
